import type { Journey } from "../../types/journey";

export const IUI_JOURNEY: Journey = {
	id: "iui",
	type: "iui",
	name: "IUI Journey",
	treatmentType: "IUI Treatment",
	totalSteps: 7,
	steps: [
		{
			id: "iui-step-1-initial-consultation-assessment",
			order: 1,
			title: "Initial Consultation & Assessment",
			description:
				"This step establishes your baseline and introduces the IUI pathway. Bring your medical history, meet your RE, and leave with a personalised plan.",
			estimatedDateRange: "[Pre-cycle] Est. Week 1 – Week 2",
			status: "completed",
			subSteps: [
				{
					id: "iui-step-1-initial-consultation-assessment-ss-01",
					title: "Prepare documentation & records",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss-02",
					title: "Book & attend consultation",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss-03",
					title: "Review outcomes & next steps",
				},
			],
			highlight: {
				dayCount: "7–14 days",
				monitoringSchedule:
					"Prepare medical history before appointment; Review treatment plan within 24 hrs of consultation",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"A first fertility consultation covers a lot of ground. Writing questions down beforehand helps many people feel more prepared and in control.",
				milestone: "Treatment Plan Received, est. Week 2",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
				monitoringFrequency: "1 visit",
				nextEvent: "complete",
			},
			tasks: [
				{
					id: "iui-step-1-initial-consultation-assessment-ss1-task1",
					title: "Fill: Patient Intake Form",
					description: "Personal details for your clinic file",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-01",
					subStepTitle: "Prepare documentation & records",
					progressTotal: 8,
					progressUnit: "fields",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss1-task2",
					title: "Fill: Health Questionnaire",
					description: "Medical history information for your next visit",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-01",
					subStepTitle: "Prepare documentation & records",
					progressTotal: 12,
					progressUnit: "fields",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss1-task3",
					title: "Upload: Previous Medical Records",
					description: "Prior treatment history and findings",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss1-task4",
					title: "Upload: Referral Letter",
					description: "Letter from your GP or referring physician",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss1-task5",
					title: "Upload: ID Documents",
					description: "Passport or government-issued photo ID",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-01",
					subStepTitle: "Prepare documentation & records",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss2-task1",
					title: "Book: Initial Consultation with RE",
					description: "First appointment with your fertility specialist",
					category: "clinical",
					deadline: "As Soon as Possible",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-02",
					subStepTitle: "Book & attend consultation",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss2-task2",
					title: "Fill: Pre-Treatment Assessment",
					description: "Baseline health and lifestyle details for your team",
					category: "clinical",
					deadline: "Before Your First Appointment",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-02",
					subStepTitle: "Book & attend consultation",
					progressTotal: 6,
					progressUnit: "fields",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss3-task1",
					title: "Review & Sign: Treatment Plan Overview",
					description: "Your personalised IUI plan for review",
					category: "clinical",
					deadline: "Within 24 Hours of Consultation",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-03",
					subStepTitle: "Review outcomes & next steps",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss3-task2",
					title: "Fill: Billing Details",
					description: "Insurance and payment information for your file",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-03",
					subStepTitle: "Review outcomes & next steps",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-ss3-task3",
					title: "Upload: Insurance Authorisation",
					description: "Pre-approval letter from your insurance provider",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-1-initial-consultation-assessment-ss-03",
					subStepTitle: "Review outcomes & next steps",
				},
			],
			faqs: [
				{
					id: "iui-step-1-initial-consultation-assessment-faq-1",
					question: "What makes IUI different from IVF?",
					answer:
						"IUI — Intrauterine Insemination — places prepared sperm directly into the uterus at the time of ovulation, working with a natural or mildly stimulated cycle. IVF involves retrieving eggs from the ovaries and fertilising them in the lab. IUI is less invasive, less expensive, and involves fewer medications, but generally has a lower success rate per cycle. Your RE will discuss which is more appropriate based on your specific situation and test results.",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-faq-2",
					question: "Who is IUI most likely to help?",
					answer:
						"IUI tends to be most effective for people with unexplained infertility, mild male factor issues, cervical factor concerns, or those using donor sperm. It's often recommended as a first-line treatment before moving to IVF. Success rates vary based on age, cause of infertility, and whether the cycle is natural or medicated. Your RE will give a realistic picture of expected outcomes based on your individual profile.",
				},
				{
					id: "iui-step-1-initial-consultation-assessment-faq-3",
					question: "How many IUI cycles are usually recommended?",
					answer:
						"Most clinics suggest attempting 3–6 cycles before reassessing or considering IVF, though this varies based on age and individual circumstances. Each cycle provides information — if multiple cycles don't result in pregnancy, the team will review whether the approach or diagnosis needs updating. The decision to move on is a clinical and personal one, and your RE will guide that conversation with you.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Initial Consultation & Assessment.",
		},
		{
			id: "iui-step-2-fertility-testing",
			order: 2,
			title: "Fertility Testing",
			description:
				"This step builds a fertility baseline through blood tests, scans, and uterine and sperm assessments — giving the team what they need to design the right IUI protocol.",
			estimatedDateRange:
				"Est. Apr 1 – Apr 14, 2026 [Pre-cycle, CD2–CD3 for baseline scan]",
			estimatedStartDate: "2026-04-01",
			status: "completed",
			subSteps: [
				{
					id: "iui-step-2-fertility-testing-ss-01",
					title: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss-02",
					title: "Uterine assessment (if indicated)",
				},
				{
					id: "iui-step-2-fertility-testing-ss-03",
					title: "Partner / sperm testing (if applicable)",
				},
			],
			highlight: {
				dayCount: "7–14 days",
				monitoringSchedule:
					"Attend baseline scan on CD2 or CD3; Notify clinic immediately when period starts (Day 1)",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Waiting for results can feel uncertain. Most come back within a few days, and every result — whatever it shows — helps the team build a better plan.",
				milestone: "All Results In, est. Apr 14",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
				monitoringFrequency: "2–3 visits",
				nextEvent: "complete",
			},
			tasks: [
				{
					id: "iui-step-2-fertility-testing-ss1-task1",
					title: "Book: AMH Blood Test",
					description: "Measure your ovarian reserve before treatment",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss1-task2",
					title: "Test: AMH Blood Test",
					description: "Ovarian reserve blood work for your protocol",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss1-task3",
					title: "Upload: AMH Test Results",
					description: "Ovarian reserve blood work results",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss1-task4",
					title: "Notify: Period Started — Day 1",
					description: "Log cycle day 1 to schedule your baseline scan",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss1-task5",
					title: "Book: Baseline Scan",
					description: "Cycle day 2–3 scan and hormone panel",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss1-task6",
					title: "Test: Hormone Panel — E2, FSH, LH & AFC",
					description: "Baseline hormones and antral follicle count",
					category: "clinical",
					deadline: "Cycle Day 2 or 3",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-2-fertility-testing-ss-01",
					subStepTitle: "Ovarian reserve & hormone testing",
				},
				{
					id: "iui-step-2-fertility-testing-ss2-task1",
					title: "Book: Uterine Assessment",
					description: "Assess uterine cavity before IUI cycle",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-2-fertility-testing-ss-02",
					subStepTitle: "Uterine assessment (if indicated)",
				},
				{
					id: "iui-step-2-fertility-testing-ss2-task2",
					title: "Test: Uterine Assessment",
					description: "Saline sonogram or hysteroscopy",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-2-fertility-testing-ss-02",
					subStepTitle: "Uterine assessment (if indicated)",
				},
				{
					id: "iui-step-2-fertility-testing-ss3-task1",
					title: "Book: Semen Analysis",
					description: "Partner sperm test — coordinate with your clinic",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-2-fertility-testing-ss-03",
					subStepTitle: "Partner / sperm testing (if applicable)",
				},
				{
					id: "iui-step-2-fertility-testing-ss3-task2",
					title: "Test: Semen Analysis",
					description: "Partner sperm count, motility and morphology",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-2-fertility-testing-ss-03",
					subStepTitle: "Partner / sperm testing (if applicable)",
				},
				{
					id: "iui-step-2-fertility-testing-ss3-task3",
					title: "Upload: Semen Analysis Report",
					description: "Partner lab results for your shared file",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-2-fertility-testing-ss-03",
					subStepTitle: "Partner / sperm testing (if applicable)",
				},
			],
			faqs: [
				{
					id: "iui-step-2-fertility-testing-faq-1",
					question: "What tests are needed before starting IUI?",
					answer:
						"The standard pre-IUI workup typically includes an AMH blood test and antral follicle count to assess ovarian reserve, a day-2 or day-3 hormone panel, and a uterine assessment to confirm the cavity is suitable. If a partner is involved, a semen analysis is also required. Some clinics also recommend carrier screening. The specific tests ordered will depend on your history and what the RE wants to rule out before beginning.",
				},
				{
					id: "iui-step-2-fertility-testing-faq-2",
					question: "Does the uterine assessment hurt?",
					answer:
						"A saline sonogram involves passing a small amount of saline into the uterus while performing an ultrasound. It typically causes mild cramping, similar to period cramps, that resolves quickly. A hysteroscopy, if needed, is more involved but still usually done as an outpatient procedure. Taking over-the-counter pain relief beforehand, as directed by the clinic, tends to help. The discomfort is brief and the information it provides is genuinely important for planning.",
				},
				{
					id: "iui-step-2-fertility-testing-faq-3",
					question: "What if semen analysis results show a problem?",
					answer:
						"A semen analysis showing lower-than-expected count, motility, or morphology doesn't automatically rule out IUI — it helps determine which treatment pathway is most appropriate. Mild male factor concerns are often still treatable with IUI using washed and concentrated sperm. More significant findings might lead the team to recommend IVF with ICSI instead. The RE will review all results together before making a protocol recommendation.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Fertility Testing.",
		},
		{
			id: "iui-step-3-pre-treatment-optimization",
			order: 3,
			title: "Pre-Treatment Optimization",
			description:
				"Before the cycle begins, this step covers consents, financial setup, and the decision between a natural or medicated IUI cycle — so everything is ready before Day 1.",
			estimatedDateRange: "Est. Apr 14 – Apr 20, 2026 [Pre-cycle, before CD1]",
			estimatedStartDate: "2026-04-14",
			status: "completed",
			subSteps: [
				{
					id: "iui-step-3-pre-treatment-optimization-ss-01",
					title: "Financial & insurance setup",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss-02",
					title: "Consent documentation",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss-03",
					title: "Cycle type & protocol setup",
				},
			],
			highlight: {
				dayCount: "5–7 days",
				monitoringSchedule:
					"Sign all consent forms before cycle day 1; Confirm natural vs. medicated cycle with RE",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Choosing between a natural and medicated cycle is worth discussing carefully with your RE. Both are valid — the right choice depends on ovarian reserve, cycle history, and personal preferences.",
				milestone: "Protocol Signed, est. Apr 20",
				currentDayLabel: "Day [N] of ~7",
				estimatedLengthDays: 7,
				monitoringFrequency: "1 visit",
				nextEvent: "complete",
			},
			tasks: [
				{
					id: "iui-step-3-pre-treatment-optimization-ss1-task1",
					title: "Fill: Insurance Verification Form",
					description: "Confirm your IUI coverage with your provider",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-01",
					subStepTitle: "Financial & insurance setup",
					progressTotal: 4,
					progressUnit: "fields",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss1-task2",
					title: "Book: Insurance Agent Call",
					description: "Discuss your coverage for IUI treatment",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-01",
					subStepTitle: "Financial & insurance setup",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss1-task3",
					title: "Review & Sign: Financial Agreement",
					description: "Treatment cost breakdown and payment terms",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-01",
					subStepTitle: "Financial & insurance setup",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss2-task1",
					title: "Review & Sign: General Consent Form",
					description: "Standard clinic terms and permissions",
					category: "clinical",
					deadline: "Before Treatment Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss2-task2",
					title: "Review & Sign: Informed Consent for IUI",
					description: "IUI procedure risks, benefits and alternatives",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-02",
					subStepTitle: "Consent documentation",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss3-task1",
					title: "Review & Sign: Treatment Protocol",
					description: "Natural vs medicated cycle plan",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-03",
					subStepTitle: "Cycle type & protocol setup",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss3-task2",
					title: "Review & Sign: Medication Consent",
					description: "Acknowledge medication risks and dosage plan",
					category: "medication",
					deadline: "Before Stimulation Starts",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-03",
					subStepTitle: "Cycle type & protocol setup",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-ss3-task3",
					title: "Book: Coordinator Check-in",
					description: "Confirm plan and next steps before cycle starts",
					category: "clinical",
					deadline: "Before Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-3-pre-treatment-optimization-ss-03",
					subStepTitle: "Cycle type & protocol setup",
				},
			],
			faqs: [
				{
					id: "iui-step-3-pre-treatment-optimization-faq-1",
					question: "What is the difference between natural and medicated IUI?",
					answer:
						"A natural IUI cycle tracks ovulation without medication and inseminates at the time of the natural LH surge. A medicated cycle uses low-dose oral or injectable medications to stimulate one or two follicles and then times the trigger shot to control ovulation precisely. Medicated cycles generally have slightly higher success rates but also carry a small risk of multiple pregnancy. Your RE will recommend based on your age, diagnosis, and ovarian reserve.",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-faq-2",
					question: "What do the consent forms cover for IUI?",
					answer:
						"IUI consent covers the procedure itself — including risks such as mild cramping, spotting, and a small risk of infection — as well as the use of donor sperm if applicable and any medications used in the cycle. If a known donor is involved, separate legal consent is also required. As with any medical consent, reading carefully and asking questions before signing is encouraged rather than just going through the motions.",
				},
				{
					id: "iui-step-3-pre-treatment-optimization-faq-3",
					question: "Is injection training needed for IUI?",
					answer:
						"If the protocol includes injectable gonadotropins, injection training is recommended. For oral medication cycles or natural cycles, no training is needed. The nurse will clarify at the protocol review appointment what, if any, medications will be self-administered and provide training if required.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Pre-Treatment Optimization.",
		},
		{
			id: "iui-step-4-cycle-monitoring-ovulation-tracking",
			order: 4,
			title: "Cycle Monitoring & Ovulation Tracking",
			description:
				"This step tracks follicle development and times the insemination precisely to ovulation — either by monitoring the natural LH surge or triggering ovulation with an injection.",
			estimatedDateRange: "Est. Apr 21 – May 2, 2026 [CD1–CD14]",
			estimatedStartDate: "2026-04-21",
			status: "completed",
			subSteps: [
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-01",
					title: "Cycle start & baseline",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					title: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-03",
					title: "Trigger shot (if medicated)",
				},
			],
			highlight: {
				dayCount: "10–14 days",
				monitoringSchedule:
					"Notify clinic immediately when period starts (Day 1); Track LH surge daily from CD10 if natural cycle; Attend monitoring scan on CD10–12 if medicated",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Timing is the most critical element of IUI. It's common to feel anxious about getting it right — the clinic will give clear instructions for exactly when to test, trigger, and come in.",
				milestone: "LH Surge or Trigger Shot, est. May 2",
				currentDayLabel: "Day [N] of ~12",
				estimatedLengthDays: 12,
				monitoringFrequency: "every 2–3 days",
				nextEvent: "complete",
			},
			tasks: [
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss1-task1",
					title: "Notify: Period Started — Day 1",
					description: "Log cycle day 1 to start your protocol",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss1-task2",
					title: "Book: Baseline Scan — Cycle Day 2–3",
					description: "Confirm ovaries are clear and ready",
					category: "cycle",
					deadline: "On Cycle Day 1",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss1-task3",
					title: "Test: Baseline Scan",
					description: "Ovaries and lining check before IUI cycle begins",
					category: "clinical",
					deadline: "Cycle Day 2 or 3",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-01",
					subStepTitle: "Cycle start & baseline",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss2-task1",
					title: "Monitor: Cycle Symptoms",
					description: "Track LH surge signs, mucus and cramping daily",
					category: "cycle",
					deadline: "Cycle Days 10–16",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					subStepTitle: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss2-task2",
					title: "Test: At-Home Ovulation Test",
					description: "Track your LH surge — natural cycle only",
					category: "cycle",
					deadline: "Cycle Days 10–16",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					subStepTitle: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss2-task3",
					title: "Book: Monitoring Scan — Cycle Day 10–12",
					description: "Follicle growth check — medicated cycle",
					category: "clinical",
					deadline: "By Cycle Day 9",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					subStepTitle: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss2-task4",
					title: "Test: Monitoring Visit — Scan & Blood Work",
					description: "Follicle size and LH/E2 levels check",
					category: "clinical",
					deadline: "Cycle Day 10–12",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					subStepTitle: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss2-task5",
					title: "Monitor: Hormone Levels",
					description: "Track E2 and LH trends between monitoring visits",
					category: "clinical",
					deadline: "Cycle Days 10–14",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-02",
					subStepTitle: "Ovulation tracking",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-ss3-task1",
					title: "Notify: Trigger Shot Done",
					description: "Confirm your hCG injection was given on time",
					category: "medication",
					deadline: "Same Night as Trigger Shot",
					status: "completed",
					progress: 100,
					source: "clinic",
					iconName: "notify",
					subStepId: "iui-step-4-cycle-monitoring-ovulation-tracking-ss-03",
					subStepTitle: "Trigger shot (if medicated)",
				},
			],
			faqs: [
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-faq-1",
					question: "How do I know when to do the IUI?",
					answer:
						"In a natural cycle, an at-home LH test detects the hormone surge that signals ovulation is approaching — IUI is typically performed 24–36 hours after the surge is detected. In a medicated cycle, a trigger shot is given when the lead follicle reaches the right size on ultrasound, and IUI is scheduled approximately 36 hours later. The clinic will give specific instructions based on which protocol is being followed.",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-faq-2",
					question: "What does a monitoring scan involve?",
					answer:
						"A monitoring scan is a transvaginal ultrasound that checks the size and number of developing follicles and the thickness of the uterine lining. It takes about 15–20 minutes at the clinic. For medicated cycles, 1–3 monitoring scans across the cycle are typical. The results determine whether the medication dose needs adjusting and confirm the right timing for the trigger shot and insemination.",
				},
				{
					id: "iui-step-4-cycle-monitoring-ovulation-tracking-faq-3",
					question: "Can the IUI cycle be cancelled?",
					answer:
						"Yes — cycles are occasionally cancelled if the ovaries don't respond to medication as expected, if too many follicles develop, or if the LH surge occurs earlier or later than anticipated. While a cancelled cycle is disappointing, it provides useful information that helps the team adjust the approach for the next attempt. Cancellation is a precautionary decision, not a sign that treatment won't work.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Cycle Monitoring & Ovulation Tracking.",
		},
		{
			id: "iui-step-5-insemination-day",
			order: 5,
			title: "Insemination Day",
			description:
				"This is the procedure day. Prepared sperm is placed directly into the uterus through a thin catheter — a quick, low-discomfort procedure that usually takes under 15 minutes.",
			estimatedDateRange:
				"Est. May 3, 2026 [~36 hrs after LH surge or trigger shot]",
			estimatedStartDate: "2026-05-03",
			status: "current",
			subSteps: [
				{
					id: "iui-step-5-insemination-day-ss-01",
					title: "Pre-procedure preparation",
				},
				{
					id: "iui-step-5-insemination-day-ss-02",
					title: "Sperm preparation & procedure",
				},
				{
					id: "iui-step-5-insemination-day-ss-03",
					title: "Post-procedure monitoring",
				},
			],
			highlight: {
				dayCount: "1 days",
				monitoringSchedule:
					"Complete pre-procedure checklist the day before; Arrange to rest lightly after the procedure",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"IUI day can feel both momentous and anticlimactic — the procedure itself is brief. Many people feel emotional, relieved, or both. That's entirely normal.",
				milestone: "IUI Procedure Complete, est. May 3",
				currentDayLabel: "Day 1 of 1",
				estimatedLengthDays: 1,
				monitoringFrequency: "1 visit",
				nextEvent: "today, IUI procedure 10:00 AM",
			},
			tasks: [
				{
					id: "iui-step-5-insemination-day-ss1-task1",
					title: "Fill: Pre-Procedure Checklist",
					description: "Arrival time and instructions confirmed",
					category: "clinical",
					deadline: "Day Before IUI",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-5-insemination-day-ss-01",
					subStepTitle: "Pre-procedure preparation",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "iui-step-5-insemination-day-ss1-task2",
					title: "Book: IUI Procedure",
					description: "Schedule your insemination appointment at clinic",
					category: "clinical",
					deadline: "~36 Hours After Trigger or LH Surge",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-5-insemination-day-ss-01",
					subStepTitle: "Pre-procedure preparation",
				},
				{
					id: "iui-step-5-insemination-day-ss2-task1",
					title: "Test: Sperm Preparation at Lab",
					description: "Lab washes and prepares sperm before insemination",
					category: "clinical",
					deadline: "IUI Procedure Day",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-5-insemination-day-ss-02",
					subStepTitle: "Sperm preparation & procedure",
				},
				{
					id: "iui-step-5-insemination-day-ss2-task2",
					title: "Test: IUI Procedure",
					description: "Catheter placement and sperm deposited into uterus",
					category: "clinical",
					deadline: "IUI Procedure Day",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-5-insemination-day-ss-02",
					subStepTitle: "Sperm preparation & procedure",
				},
				{
					id: "iui-step-5-insemination-day-ss3-task1",
					title: "Monitor: Post-IUI Symptoms",
					description: "Log mild cramping and spotting after procedure",
					category: "clinical",
					deadline: "24–48 Hours Post IUI",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-5-insemination-day-ss-03",
					subStepTitle: "Post-procedure monitoring",
				},
				{
					id: "iui-step-5-insemination-day-ss3-task2",
					title: "Review & Sign: Progesterone Support Protocol",
					description: "Luteal phase support instructions if prescribed",
					category: "medication",
					deadline: "IUI Procedure Day",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "review-sign",
					subStepId: "iui-step-5-insemination-day-ss-03",
					subStepTitle: "Post-procedure monitoring",
				},
			],
			faqs: [
				{
					id: "iui-step-5-insemination-day-faq-1",
					question: "What happens on IUI procedure day?",
					answer:
						"The sperm sample is collected and processed in the lab — concentrating the motile sperm and removing seminal fluid. The prepared sample is then drawn into a thin catheter, which is passed gently through the cervix into the uterus. The sperm is deposited and the catheter is removed. The process takes about 10–15 minutes. Mild cramping is common immediately after. Most people can go about their day normally.",
				},
				{
					id: "iui-step-5-insemination-day-faq-2",
					question: "Does IUI hurt?",
					answer:
						"Most people describe IUI as mildly uncomfortable rather than painful — similar to a smear test or mild menstrual cramp. The catheter passing through the cervix can cause a brief pinch or cramp. Taking an over-the-counter painkiller 30–60 minutes before the appointment, if the clinic recommends it, can help reduce discomfort.",
				},
				{
					id: "iui-step-5-insemination-day-faq-3",
					question: "What should I do after the procedure?",
					answer:
						"There is no evidence that lying down after IUI improves outcomes. Normal gentle activity for the rest of the day is fine. Strenuous exercise and swimming are usually avoided for 24–48 hours. All medications should be continued as prescribed. Any prescribed progesterone support should be started on the day directed by the clinic.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Insemination Day.",
		},
		{
			id: "iui-step-6-two-week-wait",
			order: 6,
			title: "Two-Week Wait",
			description:
				"This step is the waiting period between insemination and the pregnancy blood test. Continue any medications, log symptoms, and access support whenever needed.",
			estimatedDateRange: "Est. May 3 – May 17, 2026 [Day 1–14 post IUI]",
			estimatedStartDate: "2026-05-03",
			status: "future",
			subSteps: [
				{
					id: "iui-step-6-two-week-wait-ss-01",
					title: "Progesterone support (if prescribed)",
				},
				{
					id: "iui-step-6-two-week-wait-ss-02",
					title: "Symptom & wellbeing monitoring",
				},
				{
					id: "iui-step-6-two-week-wait-ss-03",
					title: "Emotional support",
				},
				{
					id: "iui-step-6-two-week-wait-ss-04",
					title: "Beta hCG test booking",
				},
			],
			highlight: {
				dayCount: "14 days",
				monitoringSchedule:
					"Continue progesterone support if prescribed — do not stop; Log symptoms daily in the app; Book Beta hCG test at 14 days post IUI",
				staffInfo: "Nurse B. Chen",
				headsUpNote:
					"The two-week wait after IUI carries the same emotional intensity as after IVF. Symptoms cannot predict the result. It's common to find this period genuinely difficult.",
				milestone: "Beta hCG Test, est. May 17",
				currentDayLabel: "Day [N] of ~14",
				estimatedLengthDays: 14,
				monitoringFrequency: "daily logging",
				nextEvent: "May 17, Beta hCG test",
			},
			tasks: [
				{
					id: "iui-step-6-two-week-wait-ss1-task1",
					title: "Monitor: Medication Side Effects",
					description: "Log your daily response to progesterone support",
					category: "medication",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-6-two-week-wait-ss-01",
					subStepTitle: "Progesterone support (if prescribed)",
				},
				{
					id: "iui-step-6-two-week-wait-ss2-task1",
					title: "Monitor: TWW Symptoms",
					description: "Track cramping, spotting and early pregnancy signs",
					category: "clinical",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-6-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "iui-step-6-two-week-wait-ss2-task2",
					title: "Monitor: Wellbeing & Mood",
					description: "Daily emotional check-in during the two-week wait",
					category: "cycle",
					deadline: "Daily During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "monitor",
					subStepId: "iui-step-6-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "iui-step-6-two-week-wait-ss2-task3",
					title: "Notify: New Symptom or Side Effect",
					description: "Report heavy bleeding or severe pain immediately",
					category: "clinical",
					deadline: "As Soon as It Occurs",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "notify",
					subStepId: "iui-step-6-two-week-wait-ss-02",
					subStepTitle: "Symptom & wellbeing monitoring",
				},
				{
					id: "iui-step-6-two-week-wait-ss3-task1",
					title: "Book: Emotional Support Session",
					description: "Talk to a counsellor on your care team",
					category: "cycle",
					deadline: "Anytime During TWW",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-6-two-week-wait-ss-03",
					subStepTitle: "Emotional support",
				},
				{
					id: "iui-step-6-two-week-wait-ss4-task1",
					title: "Book: Pregnancy Blood Test — Beta hCG",
					description: "Confirm pregnancy after your insemination",
					category: "clinical",
					deadline: "14 Days Post IUI",
					status: "ongoing",
					progress: 0,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-6-two-week-wait-ss-04",
					subStepTitle: "Beta hCG test booking",
				},
			],
			faqs: [
				{
					id: "iui-step-6-two-week-wait-faq-1",
					question: "Can I take a home pregnancy test during the wait?",
					answer:
						"If a trigger shot was used, home tests may give a false positive for several days as the hCG clears the system. Even without a trigger shot, testing early often gives inconclusive or misleading results since implantation takes several days after insemination. The clinical blood test at day 14 is more accurate and gives a quantitative hCG level rather than just a yes or no.",
				},
				{
					id: "iui-step-6-two-week-wait-faq-2",
					question: "What symptoms are common after IUI?",
					answer:
						"Mild cramping and light spotting in the first 24–48 hours are common after the procedure itself. After that, any symptoms experienced are largely driven by progesterone support medication if prescribed, rather than by whether implantation has occurred. Breast tenderness, bloating, and fatigue can appear regardless of outcome. Heavy bleeding, fever, or severe pain should be reported to the clinic promptly.",
				},
				{
					id: "iui-step-6-two-week-wait-faq-3",
					question: "How do people manage the emotional weight of waiting?",
					answer:
						"Staying gently occupied, maintaining normal routines where possible, and having someone to talk to are strategies that help many people. The emotional support session built into this step is available to access without needing a specific reason. Limiting time on online forums where results are not representative of typical experience is also something many find genuinely helpful.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Two-Week Wait.",
		},
		{
			id: "iui-step-7-pregnancy-test-next-steps",
			order: 7,
			title: "Pregnancy Test & Next Steps",
			description:
				"Today is the day of the pregnancy blood test. Whatever the result, the care team is here — to celebrate and plan next steps, or to review and look forward together.",
			estimatedDateRange: "Est. May 17, 2026 [Day 14 post IUI]",
			estimatedStartDate: "2026-05-17",
			status: "future",
			subSteps: [
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss-01",
					title: "Beta hCG blood test",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss-02",
					title: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss-03",
					title: "Negative outcome — cycle review",
				},
			],
			highlight: {
				dayCount: "1–3 days",
				monitoringSchedule:
					"Attend Beta hCG blood test on scheduled date — do not test early; Upload result and notify clinic same day",
				staffInfo: "Dr. T. Miller | Nurse B. Chen",
				headsUpNote:
					"Today brings a significant result — and significant feelings either way. Having someone available, whatever the outcome, is something many people find important.",
				milestone: "Beta hCG Result, est. May 17",
				currentDayLabel: "Day [N] of ~3",
				estimatedLengthDays: 3,
				monitoringFrequency: "1–2 visits",
				nextEvent: "May 17, Beta hCG result",
			},
			tasks: [
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss1-task1",
					title: "Test: Pregnancy Blood Test — Beta hCG",
					description: "Confirm pregnancy after your insemination",
					category: "clinical",
					deadline: "14 Days Post IUI",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss1-task2",
					title: "Upload: Beta hCG Result",
					description: "Result triggers positive or negative pathway",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "upload",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss1-task3",
					title: "Notify: Test Results Received",
					description: "Let your team know your Beta hCG results are in",
					category: "clinical",
					deadline: "Same Day Results Arrive",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "notify",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-01",
					subStepTitle: "Beta hCG blood test",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss2-task1",
					title: "Book: Confirmation Ultrasound Scan",
					description: "Heartbeat confirmation if Beta hCG is positive",
					category: "clinical",
					deadline: "~6–7 Weeks Gestation",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss2-task2",
					title: "Test: Confirmation Ultrasound Scan",
					description: "Confirm gestational sac, fetal pole and heartbeat",
					category: "clinical",
					deadline: "~6–7 Weeks Gestation",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "test",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss2-task3",
					title: "Book: OB Handoff Appointment",
					description: "Transfer of care from fertility clinic to OB/GYN",
					category: "clinical",
					deadline: "~8–10 Weeks Gestation",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-02",
					subStepTitle: "Positive outcome — confirmation & OB handoff",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss3-task1",
					title: "Fill: Cycle Review Form",
					description: "Document your cycle outcomes for clinical review",
					category: "clinical",
					deadline: "Within 1 Week of Result",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "fill",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-03",
					subStepTitle: "Negative outcome — cycle review",
					progressTotal: 5,
					progressUnit: "fields",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-ss3-task2",
					title: "Book: Cycle Review Consultation",
					description: "Discuss your outcomes and next steps with your RE",
					category: "clinical",
					deadline: "Within 2 Weeks of Result",
					status: "ongoing",
					progress: 60,
					source: "clinic",
					iconName: "book",
					subStepId: "iui-step-7-pregnancy-test-next-steps-ss-03",
					subStepTitle: "Negative outcome — cycle review",
				},
			],
			faqs: [
				{
					id: "iui-step-7-pregnancy-test-next-steps-faq-1",
					question: "What does the Beta hCG level mean after IUI?",
					answer:
						"Beta hCG is the hormone produced after a fertilised egg implants. A level above 25 mIU/mL is generally considered positive, though clinic thresholds vary. After IUI, a single result is less informative than the trend — the clinic may request a repeat test 48 hours later to confirm the level is rising appropriately. A rising level indicates a developing pregnancy; a falling level after an initial positive may indicate a chemical pregnancy.",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-faq-2",
					question: "What happens if the result is negative?",
					answer:
						"A negative result after IUI is difficult, and it's valid to need time before thinking about next steps. Your care team will schedule a cycle review — usually within 1–2 weeks — to discuss what was learned, whether any protocol changes are warranted, and whether another IUI cycle or a different approach is recommended.",
				},
				{
					id: "iui-step-7-pregnancy-test-next-steps-faq-3",
					question: "What happens after a positive result?",
					answer:
						"After a positive Beta hCG, a repeat test 48 hours later confirms the level is rising, followed by a confirmation ultrasound at around 6–7 weeks to check for a gestational sac, fetal pole, and heartbeat. If confirmed, care transfers to an OB/GYN at around 8–10 weeks. The fertility clinic will advise on when to reduce and stop progesterone support, which is typically tapered once the pregnancy is clinically confirmed.",
				},
			],
			askEvePrompt:
				"Tell me more about what to expect during my Pregnancy Test & Next Steps.",
		},
	],
};
